import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Pencil, X, GripVertical } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const fullUrl = (url) => !url ? "" : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`;
const PLACEMENTS = [["home", "Homepage"], ["collection", "Collection Pages"], ["product", "Product Pages"], ["social", "Shoppable Social"]];

function EditModal({ video, onClose, onSaved }) {
  const [f, setF] = useState({ title: video.title, description: video.description || "", order: video.order || 0, active: video.active, placement: video.placement || ["home"], thumbnail: video.thumbnail || "" });
  const [busy, setBusy] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(false);
  const thumbRef = useRef(null);
  const uploadThumb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbBusy(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/admin/media/bulk", fd);
      const url = (data.items || [])[0]?.url;
      if (url) { setF((prev) => ({ ...prev, thumbnail: url })); toast.success("Thumbnail uploaded"); }
      else toast.error((data.items || [])[0]?.error || "Upload failed");
    } catch (err) { toast.error(err.friendlyMessage); } finally { setThumbBusy(false); if (thumbRef.current) thumbRef.current.value = ""; }
  };
  const togglePlacement = (p) => setF({ ...f, placement: f.placement.includes(p) ? f.placement.filter((x) => x !== p) : [...f.placement, p] });
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/admin/videos/${video.id}`, f);
      toast.success("Video updated");
      onSaved(); onClose();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="video-edit-modal">
      <form onSubmit={save} className="bg-white w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-xl">Edit Video</p>
          <button type="button" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <label className="tq-overline !text-[10px] block mb-1">Title</label>
        <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="video-edit-title" className="tq-input mb-3" />
        <label className="tq-overline !text-[10px] block mb-1">Description</label>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} data-testid="video-edit-description" className="tq-input mb-3" />
        <label className="tq-overline !text-[10px] block mb-1">Thumbnail / Cover Image</label>
        <div className="flex items-center gap-3 mb-3">
          {f.thumbnail ? <img src={f.thumbnail} alt="" className="w-16 h-20 object-cover border border-[#E5DEC9]" data-testid="video-thumb-preview" /> : <span className="text-[11px] text-[#9a938a]">No thumbnail — first video frame is used</span>}
          <button type="button" onClick={() => thumbRef.current?.click()} disabled={thumbBusy} data-testid="video-thumb-upload" className="tq-btn-outline !py-2 !px-3"><Upload size={13} /> {thumbBusy ? "…" : "Upload"}</button>
          {f.thumbnail && <button type="button" onClick={() => setF({ ...f, thumbnail: "" })} className="text-xs text-[#8B1E21] underline">Remove</button>}
          <input ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={uploadThumb} data-testid="video-thumb-input" />
        </div>
        <label className="tq-overline !text-[10px] block mb-1">Sort Order</label>
        <input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} data-testid="video-edit-order" className="tq-input mb-3" />
        <label className="tq-overline !text-[10px] block mb-1">Placement</label>
        <div className="flex flex-wrap gap-3 mb-3">
          {PLACEMENTS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={f.placement.includes(k)} onChange={() => togglePlacement(k)} data-testid={`video-placement-${k}`} className="accent-[#D4AF37] w-4 h-4" />{l}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm mb-5 cursor-pointer">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="video-edit-active" className="accent-[#D4AF37] w-4 h-4" /> Visible on storefront
        </label>
        <div className="flex gap-2">
          <button disabled={busy} data-testid="video-edit-save" className="tq-btn-dark flex-1">{busy ? "Saving…" : "Save Changes"}</button>
          <button type="button" onClick={onClose} className="tq-btn-outline">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminVideos() {
  const [videos, setVideos] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", order: 0 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/videos").then(({ data }) => setVideos(data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("order", String(form.order));
      await api.post("/admin/videos", fd, { timeout: 120000 });
      toast.success("Video uploaded");
      setForm({ title: "", description: "", order: 0 });
      load();
    } catch (err) { toast.error(err.friendlyMessage); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); toast.success("Video deleted"); load(); }
    catch (err) { toast.error(err.friendlyMessage); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-videos">
      <div className="mb-6">
        <h1 className="font-serif text-3xl">Video Library</h1>
        <p className="text-sm text-[#6E6D6A] mt-1">Upload product reels & lookbook videos. MP4, WebM or MOV up to 200 MB.</p>
      </div>

      <div className="bg-white border border-[#D4AF37]/50 p-5 mb-6" data-testid="video-upload-panel">
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="tq-overline !text-[10px] block mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Festive Heels Lookbook" data-testid="video-title" className="tq-input" />
          </div>
          <div>
            <label className="tq-overline !text-[10px] block mb-1">Description (optional)</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="video-description" className="tq-input" />
          </div>
          <div className="w-24">
            <label className="tq-overline !text-[10px] block mb-1">Order</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} data-testid="video-order" className="tq-input" />
          </div>
        </div>
        <label className={`mt-4 flex items-center justify-center gap-2 border-2 border-dashed border-[#D4AF37]/50 p-6 cursor-pointer hover:bg-[#F7F2E7] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="video-upload-dropzone">
          <Upload size={18} className="text-[#C5A059]" />
          <span className="text-sm font-medium">{uploading ? "Uploading… please wait" : "Choose a video file to upload"}</span>
          <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} data-testid="video-file-input" />
        </label>
      </div>

      {!videos ? <div className="h-40 bg-white animate-pulse" /> : videos.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-10 text-center text-sm text-[#6E6D6A]" data-testid="videos-empty">No videos yet. Upload your first product reel above.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="bg-white border border-[#E5DEC9] group" data-testid={`video-card-${v.id}`}>
              <div className="aspect-video bg-[#121212] relative">
                <video src={fullUrl(v.url)} poster={fullUrl(v.thumbnail)} controls preload="metadata" className="w-full h-full object-contain" />
                {!v.active && <span className="absolute top-2 left-2 bg-[#8B1E21] text-white text-[9px] uppercase tracking-wider px-2 py-1">Hidden</span>}
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{v.title}</p>
                    <p className="text-[11px] text-[#9a938a] mt-0.5 flex items-center gap-1"><GripVertical size={11} /> Order {v.order} · {fmtSize(v.size)} · {(v.placement || []).join(", ") || "no placement"}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing(v)} data-testid={`video-edit-${v.id}`} aria-label="Edit video" className="p-1.5 hover:bg-[#F4EFE6]"><Pencil size={14} /></button>
                    <button onClick={() => del(v)} data-testid={`video-delete-${v.id}`} aria-label="Delete video" className="p-1.5 text-[#8B1E21] hover:bg-[#8B1E21]/10"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditModal video={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
